import React, { useState } from 'react';
import { Screen, AppState } from '../App';
import { User, getStrategiesNeedingRatingToday, getStudentById, saveDailyRating, getTodayDateKey, generateId, DailyRating as DailyRatingEntry } from '../storage';

interface Props { navigate: (s: Screen, state?: AppState) => void; user: User | null; }

export default function DailyRating({ navigate, user }: Props) {
  const strategies = user ? getStrategiesNeedingRatingToday(user.id) : [];
  const [current, setCurrent] = useState(0);
  const [done, setDone] = useState(false);
  const [ratings, setRatings] = useState<Record<string, string>>({});

  if (strategies.length === 0 || done) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center' }}>
        <p style={{ fontSize: 48, marginBottom: 16 }}>✅</p>
        <h2 style={{ marginBottom: 12 }}>All rated for today!</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
          Your ratings are building a real picture of what works for each student. Come back tomorrow.
        </p>
        <button onClick={() => navigate('dashboard')} className="btn-primary">Back to Dashboard</button>
      </div>
    );
  }

  const attempt = strategies[current];
  const student = user ? getStudentById(user.id, attempt.studentId) : null;

  const rate = (result: 'worked' | 'partially' | 'didnt-work') => {
    if (!user) return;
    const rating: DailyRatingEntry = {
      id: generateId(),
      strategyAttemptId: attempt.id,
      studentId: attempt.studentId,
      userId: user.id,
      date: Date.now(),
      dateKey: getTodayDateKey(),
      result
    };
    saveDailyRating(user.id, rating);
    setRatings(prev => ({ ...prev, [attempt.id]: result }));

    setTimeout(() => {
      if (current < strategies.length - 1) {
        setCurrent(current + 1);
      } else {
        setDone(true);
      }
    }, 800);
  };

  const rated = ratings[attempt.id];

  return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate('dashboard')} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>Back
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>Daily Strategy Rating</span>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{current + 1}/{strategies.length}</span>
      </div>

      <div className="page-content">
        {/* Progress bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>
          {strategies.map((_, i) => (
            <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i < current ? 'var(--teal)' : i === current ? 'var(--amber)' : '#e0dbd3', transition: '0.3s' }} />
          ))}
        </div>

        {/* Student + strategy */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span className="mono" style={{ fontSize: 22, color: 'var(--teal)', fontWeight: 500 }}>{student?.initials || '?'}</span>
          <span className="badge badge-teal">{student?.grade}</span>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>today's check-in</span>
        </div>

        <div style={{ background: 'var(--teal-light)', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Strategy you tried today</p>
          <p style={{ fontSize: 15, lineHeight: 1.6 }}>{attempt.strategy}</p>
        </div>

        <h2 style={{ marginBottom: 6 }}>How did it go today?</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>One tap — that's all it takes. This builds your 30-day picture.</p>

        {!rated ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button onClick={() => rate('worked')} style={{ background: 'var(--green)', color: 'white', padding: '20px', borderRadius: 14, fontSize: 17, fontWeight: 700, border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ fontSize: 28 }}>✅</span>
              <div><p style={{ margin: 0 }}>Worked</p><p style={{ fontSize: 13, fontWeight: 400, opacity: 0.9, marginTop: 2 }}>Made a positive difference today</p></div>
            </button>
            <button onClick={() => rate('partially')} style={{ background: 'var(--amber)', color: 'white', padding: '20px', borderRadius: 14, fontSize: 17, fontWeight: 700, border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ fontSize: 28 }}>🔄</span>
              <div><p style={{ margin: 0 }}>Partially</p><p style={{ fontSize: 13, fontWeight: 400, opacity: 0.9, marginTop: 2 }}>Some improvement but not consistent</p></div>
            </button>
            <button onClick={() => rate('didnt-work')} style={{ background: 'var(--coral)', color: 'white', padding: '20px', borderRadius: 14, fontSize: 17, fontWeight: 700, border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ fontSize: 28 }}>❌</span>
              <div><p style={{ margin: 0 }}>Didn't work</p><p style={{ fontSize: 13, fontWeight: 400, opacity: 0.9, marginTop: 2 }}>No change or things got harder</p></div>
            </button>
          </div>
        ) : (
          <div className="animate-in" style={{ background: rated === 'worked' ? 'var(--green-light)' : rated === 'partially' ? 'var(--amber-light)' : 'var(--coral-light)', borderRadius: 14, padding: '24px', textAlign: 'center' }}>
            <p style={{ fontSize: 32, marginBottom: 8 }}>{rated === 'worked' ? '✅' : rated === 'partially' ? '🔄' : '❌'}</p>
            <p style={{ fontWeight: 700, fontSize: 16, color: rated === 'worked' ? 'var(--green)' : rated === 'partially' ? '#b87800' : 'var(--coral)' }}>Rated! Moving to next...</p>
          </div>
        )}

        <button onClick={() => navigate('dashboard')} style={{ background: 'none', color: 'var(--text-secondary)', width: '100%', padding: '14px', marginTop: 20, fontSize: 13, border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
          Skip for now
        </button>
      </div>
    </div>
  );
}
